import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from '..';
import {userApi} from './userApiSlice';
import {IArtifact, IUser, PinTypes, Custom_Encode_URL, LogInType} from 'app/types/Shared';

export enum LogInAction {
  EnableRedirected = 'ENABLE_REDIRECTED',
  EnableLoggedIn = 'ENABLE_LOGGED_IN',
  DisableRedirected = 'DISABLE_REDIRECTED',
  DisableLoggedIn = 'DISABLE_LOGGED_IN',
  DisableAll = 'DISABLE_ALL',
  EnableAll = 'ENABLE_ALL',
}

export interface IUserState {
  user?: IUser;
  userType?: LogInType;
  previousUserType?: LogInType;
  allUserPins?: Array<IArtifact>;
  recentlyViewed: Array<IArtifact>;
  logInStatus: {
    hasRedirected: boolean;
    isLoggedIn: boolean;
  };
}

const initialState: IUserState = {
  user: undefined,
  allUserPins: [],
  recentlyViewed: [],
  userType: undefined,
  previousUserType: undefined,
  logInStatus: {
    hasRedirected: false,
    isLoggedIn: false,
  },
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, {payload}: PayloadAction<IUser>) => {
      state.user = payload;
    },
    setUserType: (state, {payload}: PayloadAction<LogInType | undefined>) => {
      state.userType = payload;
    },
    setLogInStatus: (state, {payload}: PayloadAction<LogInAction>) => {
      switch (payload) {
        case LogInAction.EnableRedirected: {
          state.logInStatus.hasRedirected = true;
          break;
        }
        case LogInAction.DisableRedirected: {
          state.logInStatus.hasRedirected = false;
          break;
        }
        case LogInAction.EnableLoggedIn: {
          state.logInStatus.isLoggedIn = true;
          break;
        }
        case LogInAction.DisableLoggedIn: {
          state.logInStatus.isLoggedIn = false;
          break;
        }
        case LogInAction.EnableAll: {
          state.logInStatus = {hasRedirected: true, isLoggedIn: true};
          break;
        }
        case LogInAction.DisableAll: {
          state.logInStatus = {hasRedirected: false, isLoggedIn: false};
          break;
        }
        default:
          break;
      }
    },
    setUserPins: (state, {payload}: PayloadAction<Array<IArtifact>>) => {
      state.allUserPins = payload;
    },
    setRecentlyViewed: (state, {payload}: PayloadAction<IArtifact>) => {
      if (!state.recentlyViewed) {
        state.recentlyViewed = [];
      }

      const duplicate = state.recentlyViewed.findIndex((X) => X.key === payload.key);
      if (duplicate !== -1) {
        state.recentlyViewed.splice(duplicate, 1);
      }
      state.recentlyViewed.unshift(payload);
    },
    setPreviousUserType: (state, {payload}: PayloadAction<LogInType | undefined>) => {
      state.previousUserType = payload;
    },
    resetUserState: (state) => {
      state.allUserPins = [];
      state.recentlyViewed = [];
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(userApi.endpoints.getUserByEmail.matchFulfilled, (state, {payload}) => {
      console.log('userSlice: completed userApi.getUserByEmail...');
      state.user = payload.user;
      const tempAllPins: Array<IArtifact> = [];
      payload.processPins.forEach((process) => {
        tempAllPins.push({
          id: 0,
          key: process.process_id,
          docid: process.process_id,
          title: process.name,
          url: `${window.location.origin}/process/${Custom_Encode_URL(process?.name)}/${process.operating_models_list}`,
          type: PinTypes.Process,
          system: 'ELEVATE',
          owner: process.owner_name,
          owner_email: process.owner_email,
          created_dt: process.created_dt,
        });
      });
      state.allUserPins = payload.artifactPins.concat(tempAllPins);
      console.log('User pins fetched: ' + state.allUserPins.length);
    });
  },
});

export const {
  setUser,
  setUserPins,
  setRecentlyViewed,
  setUserType,
  setLogInStatus,
  setPreviousUserType,
  resetUserState,
} = userSlice.actions;

export const selectUser = (state: RootState) => state.user.user;
export const selectUserLogInStatus = (state: RootState) => state.user.logInStatus;
export const selectUserType = (state: RootState) => state.user.userType;
export const selectUserPins = (state: RootState) => state.user.allUserPins;
export const selectRecentlyViewed = (state: RootState) => state.user.recentlyViewed;
export const selectPreviousUserType = (state: RootState) => state.user.previousUserType;

export default userSlice.reducer;
